import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Check, 
  X, 
  ArrowRight,
  Star,
  Zap,
  DollarSign,
  Users,
  Shield,
  Palette,
  Code
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import MorphingNavbar from '../../components/MorphingNavbar';

// ============================================
// COMPETITOR DATA
// ============================================

const COMPETITORS = [
  {
    id: 'edrano',
    name: 'Edrano',
    logo: 'E',
    tagline: 'Built for service agencies',
    startingPrice: 39,
    isUs: true,
    pros: [
      'Affordable starting price ($39/mo)',
      'Clean, focused interface',
      'Knowledge base included',
      '0% transaction fees on direct plans'
    ],
    cons: [
      'Newer platform (launched 2026)',
      'Smaller community'
    ]
  },
  {
    id: 'spp',
    name: 'SPP.co',
    logo: 'S',
    tagline: 'Service Provider Pro',
    startingPrice: 99,
    isUs: false,
    pros: [
      'Established platform',
      'Unlimited clients',
      'Strong order management'
    ],
    cons: [
      'Expensive starting price ($99/mo)',
      'Can be complex to set up',
      'No built-in knowledge base'
    ]
  },
  {
    id: 'suitedash',
    name: 'SuiteDash',
    logo: 'SD',
    tagline: 'All-in-one business software',
    startingPrice: 19,
    isUs: false,
    pros: [
      'Very affordable ($19/mo)',
      'Tons of features',
      'Unlimited everything'
    ],
    cons: [
      'Overwhelming interface',
      'Dated design',
      'Slow performance'
    ]
  },
  {
    id: 'copilot',
    name: 'Copilot',
    logo: 'C',
    tagline: 'Modern client portal',
    startingPrice: 59,
    isUs: false,
    pros: [
      'Beautiful, modern UI',
      'Modular feature selection',
      'Good for freelancers'
    ],
    cons: [
      'Per-user pricing scales up',
      'Limited for large teams',
      'Fewer automation features'
    ]
  }
];

const FEATURE_CATEGORIES = [
  {
    name: 'Core Features',
    icon: Zap,
    features: [
      { name: 'Client Portal', edrano: true, spp: true, suitedash: true, copilot: true },
      { name: 'Service Catalog', edrano: true, spp: true, suitedash: true, copilot: true },
      { name: 'Order Management', edrano: true, spp: true, suitedash: true, copilot: true },
      { name: 'Support Tickets', edrano: true, spp: true, suitedash: true, copilot: true },
      { name: 'Knowledge Base', edrano: true, spp: false, suitedash: true, copilot: 'Limited' },
      { name: 'Team Members', edrano: '5-20', spp: '5-Unlimited', suitedash: 'Unlimited', copilot: 'Per-user' }
    ]
  },
  {
    name: 'Billing & Payments',
    icon: DollarSign,
    features: [
      { name: 'Stripe Integration', edrano: true, spp: true, suitedash: false, copilot: true },
      { name: 'Recurring Billing', edrano: true, spp: true, suitedash: true, copilot: true },
      { name: 'One-Time Payments', edrano: true, spp: true, suitedash: true, copilot: true },
      { name: 'Invoicing', edrano: 'Coming', spp: true, suitedash: true, copilot: true },
      { name: 'Transaction Fees', edrano: '0%', spp: '0%', suitedash: '0%', copilot: '0%' },
      { name: 'Multi-Currency', edrano: true, spp: true, suitedash: true, copilot: true }
    ]
  },
  {
    name: 'White-Label & Branding',
    icon: Palette,
    features: [
      { name: 'Custom Domain', edrano: 'Growth+', spp: true, suitedash: true, copilot: 'Pro+' },
      { name: 'Custom Branding', edrano: true, spp: true, suitedash: true, copilot: true },
      { name: 'Remove Powered By', edrano: 'Scale', spp: 'Plus', suitedash: 'Pinnacle', copilot: 'Advanced' },
      { name: 'Custom CSS', edrano: 'Scale', spp: true, suitedash: true, copilot: 'Advanced' },
      { name: 'White-Label Emails', edrano: true, spp: true, suitedash: true, copilot: true },
      { name: 'Client Subdomains', edrano: true, spp: true, suitedash: true, copilot: false }
    ]
  },
  {
    name: 'Integrations & API',
    icon: Code,
    features: [
      { name: 'REST API', edrano: 'Growth+', spp: 'Plus', suitedash: false, copilot: 'Pro+' },
      { name: 'Webhooks', edrano: 'Growth+', spp: true, suitedash: false, copilot: 'Pro+' },
      { name: 'Zapier', edrano: 'Coming', spp: true, suitedash: false, copilot: true },
      { name: 'Slack Integration', edrano: 'Coming', spp: false, suitedash: false, copilot: true },
      { name: 'Custom Integrations', edrano: 'API', spp: 'Extensions', suitedash: 'Limited', copilot: 'API' }
    ]
  },
  {
    name: 'Security & Compliance',
    icon: Shield,
    features: [
      { name: 'SSL/TLS Encryption', edrano: true, spp: true, suitedash: true, copilot: true },
      { name: 'GDPR Compliant', edrano: true, spp: true, suitedash: true, copilot: true },
      { name: 'Data Export', edrano: true, spp: true, suitedash: true, copilot: true },
      { name: 'SOC 2 Ready', edrano: true, spp: false, suitedash: false, copilot: 'Enterprise' },
      { name: 'Isolated Databases', edrano: true, spp: false, suitedash: false, copilot: false },
      { name: '2FA', edrano: 'Coming', spp: true, suitedash: true, copilot: true }
    ]
  }
];

const PRICING_COMPARISON = [
  { tier: 'Entry', edrano: '$39/mo', spp: '$99/mo', suitedash: '$19/mo', copilot: '$59/mo' },
  { tier: 'Mid', edrano: '$79/mo', spp: '$249/mo', suitedash: '$49/mo', copilot: '$99/mo' },
  { tier: 'High', edrano: '$149/mo', spp: '$1,500/mo', suitedash: '$99/mo', copilot: 'Custom' },
  { tier: 'Annual Discount', edrano: '17%', spp: '~15%', suitedash: '~20%', copilot: '~15%' }
];

// ============================================
// COMPONENTS
// ============================================

function FeatureValue({ value }) {
  if (value === true) {
    return <Check className="w-5 h-5 text-green-600" />;
  }
  if (value === false) {
    return <X className="w-5 h-5 text-stone-300" />;
  }
  return <span className="text-sm text-stone-600">{value}</span>;
}

function ComparisonTable({ features, competitors }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full" data-testid="comparison-table">
        <thead>
          <tr>
            <th className="text-left py-3 px-4 text-sm font-medium text-stone-500">Feature</th>
            {competitors.map(c => (
              <th key={c.id} className={`text-center py-3 px-4 ${c.isUs ? 'bg-stone-900 text-white rounded-t-lg' : 'text-stone-700'}`}>
                <span className="text-sm font-semibold">{c.name}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {features.map((feature, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-stone-50' : 'bg-white'}>
              <td className="py-3 px-4 text-sm text-stone-700">{feature.name}</td>
              <td className={`text-center py-3 px-4 ${i % 2 === 0 ? 'bg-stone-100' : 'bg-stone-50'}`}>
                <div className="flex justify-center">
                  <FeatureValue value={feature.edrano} />
                </div>
              </td>
              <td className="text-center py-3 px-4">
                <div className="flex justify-center">
                  <FeatureValue value={feature.spp} />
                </div>
              </td>
              <td className="text-center py-3 px-4">
                <div className="flex justify-center">
                  <FeatureValue value={feature.suitedash} />
                </div>
              </td>
              <td className="text-center py-3 px-4">
                <div className="flex justify-center">
                  <FeatureValue value={feature.copilot} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function Compare() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F4EF' }}>
      {/* Navigation */}
      <MorphingNavbar />

      {/* Hero */}
      <section className="pt-12 pb-16" data-testid="compare-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-4">
            Edrano vs. The Competition
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            See how Edrano compares to SPP.co, SuiteDash, and Copilot. 
            We built what agencies actually need, at a price that makes sense.
          </p>
        </div>
      </section>

      {/* Quick Comparison Cards */}
      <section className="py-12" data-testid="quick-compare">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-6">
            {COMPETITORS.map((comp) => (
              <div 
                key={comp.id}
                className={`p-6 rounded-2xl ${
                  comp.isUs 
                    ? 'bg-stone-900 text-white' 
                    : 'bg-white border border-stone-200'
                }`}
                data-testid={`compare-card-${comp.id}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold mb-4 ${
                  comp.isUs ? 'bg-white text-stone-900' : 'bg-stone-100 text-stone-700'
                }`}>
                  {comp.logo}
                </div>
                <h3 className={`text-xl font-bold mb-1 ${comp.isUs ? 'text-white' : 'text-stone-900'}`}>
                  {comp.name}
                </h3>
                <p className={`text-sm mb-4 ${comp.isUs ? 'text-stone-300' : 'text-stone-500'}`}>
                  {comp.tagline}
                </p>
                <div className="mb-4">
                  <span className={`text-3xl font-bold ${comp.isUs ? 'text-white' : 'text-stone-900'}`}>
                    ${comp.startingPrice}
                  </span>
                  <span className={`text-sm ${comp.isUs ? 'text-stone-300' : 'text-stone-500'}`}>/mo</span>
                </div>
                
                <div className="space-y-2">
                  <p className={`text-xs font-medium ${comp.isUs ? 'text-stone-300' : 'text-stone-500'}`}>Pros:</p>
                  {comp.pros.slice(0, 3).map((pro, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${comp.isUs ? 'text-green-400' : 'text-green-600'}`} />
                      <span className={`text-xs ${comp.isUs ? 'text-stone-200' : 'text-stone-600'}`}>{pro}</span>
                    </div>
                  ))}
                </div>
                
                {comp.cons.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className={`text-xs font-medium ${comp.isUs ? 'text-stone-300' : 'text-stone-500'}`}>Cons:</p>
                    {comp.cons.slice(0, 2).map((con, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <X className={`w-4 h-4 flex-shrink-0 mt-0.5 ${comp.isUs ? 'text-stone-400' : 'text-stone-400'}`} />
                        <span className={`text-xs ${comp.isUs ? 'text-stone-300' : 'text-stone-500'}`}>{con}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Comparison */}
      <section className="py-12" data-testid="pricing-compare">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-stone-900 text-center mb-8">Pricing Comparison</h2>
          
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-stone-50">
                  <th className="text-left py-4 px-4 text-sm font-medium text-stone-500">Tier</th>
                  <th className="text-center py-4 px-4 bg-stone-900 text-white text-sm font-semibold">Edrano</th>
                  <th className="text-center py-4 px-4 text-sm font-medium text-stone-500">SPP.co</th>
                  <th className="text-center py-4 px-4 text-sm font-medium text-stone-500">SuiteDash</th>
                  <th className="text-center py-4 px-4 text-sm font-medium text-stone-500">Copilot</th>
                </tr>
              </thead>
              <tbody>
                {PRICING_COMPARISON.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                    <td className="py-4 px-4 text-sm font-medium text-stone-700">{row.tier}</td>
                    <td className={`text-center py-4 px-4 text-sm font-semibold ${i % 2 === 0 ? 'bg-stone-100' : 'bg-stone-200'} text-stone-900`}>
                      {row.edrano}
                    </td>
                    <td className="text-center py-4 px-4 text-sm text-stone-600">{row.spp}</td>
                    <td className="text-center py-4 px-4 text-sm text-stone-600">{row.suitedash}</td>
                    <td className="text-center py-4 px-4 text-sm text-stone-600">{row.copilot}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <p className="text-center mt-4 text-sm text-stone-500">
            * Edrano offers the best balance of features and price for growing agencies
          </p>
        </div>
      </section>

      {/* Detailed Feature Comparison */}
      <section className="py-12" data-testid="feature-compare">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-stone-900 text-center mb-8">Detailed Feature Comparison</h2>
          
          {FEATURE_CATEGORIES.map((category, catIndex) => (
            <div key={catIndex} className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                  <category.icon className="w-5 h-5 text-stone-600" />
                </div>
                <h3 className="text-lg font-semibold text-stone-900">{category.name}</h3>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <ComparisonTable features={category.features} competitors={COMPETITORS} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why Edrano */}
      <section className="py-16" data-testid="why-edrano">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-stone-900 rounded-3xl p-8 sm:p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Why agencies choose Edrano</h2>
            <p className="text-stone-300 mb-8 max-w-2xl mx-auto">
              We built Edrano because we were frustrated with existing options. 
              SPP was too expensive, SuiteDash was too complicated, and Copilot didn't scale well.
            </p>
            
            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">60%</div>
                <p className="text-sm text-stone-400">cheaper than SPP.co</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">5min</div>
                <p className="text-sm text-stone-400">average setup time</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">0%</div>
                <p className="text-sm text-stone-400">transaction fees</p>
              </div>
            </div>
            
            <Link to="/register">
              <Button className="bg-white text-stone-900 hover:bg-stone-100 rounded-full px-8 h-12 text-base font-medium">
                Start your free trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16" data-testid="compare-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-stone-900 text-center mb-8">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            {[
              {
                q: 'Can I migrate from SPP.co or SuiteDash?',
                a: 'Yes! We offer free migration assistance for teams moving from other platforms. Our support team will help you export your data and set up your Edrano portal.'
              },
              {
                q: 'How does Edrano compare to building my own portal?',
                a: 'Building a custom client portal typically costs $10,000-50,000+ and takes months. Edrano gives you the same features for $39/month, ready in 5 minutes.'
              },
              {
                q: 'What if I need a feature Edrano doesn\'t have?',
                a: 'We\'re constantly adding new features based on customer feedback. Check our public roadmap, and if you need something specific, let us know — we often build requested features within weeks.'
              },
              {
                q: 'Is there a long-term contract?',
                a: 'No contracts! Edrano is month-to-month (or annual with 17% discount). You can cancel anytime and export all your data.'
              }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-stone-900 mb-2">{item.q}</h3>
                <p className="text-stone-600 text-sm">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-stone-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <img src="/edrano-logo-dark.png" alt="Edrano" className="w-8 h-8 rounded-lg" />
              <span className="font-semibold text-stone-900">Edrano</span>
            </div>
            <p className="text-sm text-stone-500">
              © {new Date().getFullYear()} Edrano. All rights reserved.
              <span className="mx-2">•</span>
              Part of <a href="https://themelio.app" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">Themelio</a>
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-sm text-stone-500 hover:text-stone-900">Privacy</Link>
              <Link to="/terms" className="text-sm text-stone-500 hover:text-stone-900">Terms</Link>
              <Link to="/docs" className="text-sm text-stone-500 hover:text-stone-900">Docs</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
